import * as React from "react";
import { useEffect, useMemo, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  createEnhancedSalesPerformanceDashboard,
  monitorAndRepairCharts,
  createEnhancedSalesPerformanceDashboardWithParams as filteredDashboard,
} from "../dashboard";
import { generateInventoryDashboard } from "../inventory";
import { generateMarketingDashboard } from "../marketing";
import { DEFAULT_PROMPT, STORAGE_KEYS, FILTER_UI_SOURCES, POLLING } from "../../shared/constants";
import type { PromptHistoryItem } from "../../shared/apiService";
import {
  initializeFilterStateManager,
  clearAllFilterStates,
} from "../../shared/filterStateManager";
import { useAnalysisGuard } from "./useAnalysisGuard";
import {
  getPromptHistory,
  getInsightsHistory,
  savePromptToHistory,
  clearPromptHistory as clearPromptHistoryApi,
} from "../../shared/apiService";

export interface UseAppControllerState {
  isRefreshing: boolean;
  isGeneratingDashboard: boolean;
  lastUpdated: string;
  activeDashboard: string | null;
  dashboardStatus: string;
  asinSkuValue: string;
  startDate: Date | null;
  endDate: Date | null;
  showFilterSuccess: boolean;
  editPromptValue: string;
  promptHistory: PromptHistoryItem[];
  savedPromptTemplates: PromptHistoryItem[];
  savedPromptName: string;
  savedPromptsRefresh: number;
}

export interface UseAppControllerActions {
  setAsinSkuValue: (v: string) => void;
  setStartDate: (v: Date | null) => void;
  setEndDate: (v: Date | null) => void;
  setSavedPromptName: (v: string) => void;
  setEditPromptValue: (v: string) => void;
  setPromptHistory: React.Dispatch<React.SetStateAction<PromptHistoryItem[]>>;
  setDashboardStatus: (v: string) => void;
  handleRefreshData: () => Promise<void>;
  handleGenerateDashboard: (dashboardType: string) => Promise<void>;
  handleASINSKUSubmit: () => Promise<void>;
  handleCustomDateRangeSubmit: () => Promise<void>;
  openInsightsDialogWithPrompt: (
    prompt: string,
    dataKey?: string | null,
    shortName?: string
  ) => Promise<void>;
  loadPromptDataIfNeeded: () => Promise<void>;
}

interface DashboardMapping {
  [sheetId: string]: {
    type: string;
    route: string;
    originalName: string;
  };
}

export interface UseAppController extends UseAppControllerState, UseAppControllerActions {
  hasDateRange: boolean;
  formatDate: (d: Date | null) => string;
  savePromptHistory: (items: PromptHistoryItem[]) => Promise<void>;
  setSavedPromptsRefresh: React.Dispatch<React.SetStateAction<number>>;
}

export const streamOptions = ["Sales", "Inventory"];

export function useAppController(): UseAppController {
  const history = useHistory();
  const { isAnalysisInProgress } = useAnalysisGuard();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isGeneratingDashboard, setIsGeneratingDashboard] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("Dec 16, 2024 2:30 PM");
  const [activeDashboard, setActiveDashboard] = useState<string | null>(null);
  const [dashboardStatus, setDashboardStatus] = useState<string>("");
  const [asinSkuValue, setAsinSkuValue] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showFilterSuccess, setShowFilterSuccess] = useState(false);
  const [editPromptValue, setEditPromptValue] = useState("");
  const [promptHistory, setPromptHistory] = useState<PromptHistoryItem[]>([]);
  const [savedPromptTemplates, setSavedPromptTemplates] = useState<PromptHistoryItem[]>([]);
  const [savedPromptName, setSavedPromptName] = useState<string>("");
  const [savedPromptsRefresh, setSavedPromptsRefresh] = useState<number>(0);
  const [dashboardMappings, setDashboardMappings] = useState<DashboardMapping>({});
  const [hasLoadedPromptData, setHasLoadedPromptData] = useState(false);

  // Add refs to prevent excessive calls
  const mappingsLoadedRef = useRef(false);
  const isDetectingRef = useRef(false);
  const userNavigationRef = useRef(false); // Track if user explicitly navigated to a filter page
  const analysisInProgressRef = useRef(false); // Ref mirror of context to avoid stale closures in onActivated handler

  // Keep ref in sync with context so async onActivated handler always reads current value
  React.useEffect(() => {
    analysisInProgressRef.current = isAnalysisInProgress;
  }, [isAnalysisInProgress]);

  React.useEffect(() => {
    OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASH_FILTER);

    // Initialize the filter state manager (do not clear persisted filters)
    initializeFilterStateManager().catch((error) => {
      console.error("Failed to initialize filter state manager:", error);
    });
  }, []);

  // Listen for hash changes to detect user navigation to filter pages
  React.useEffect(() => {
    const handleHashChange = () => {
      const currentHash = window.location.hash;
      const filterRoutes = [
        "#/asin-filter",
        "#/custom-date-range",
        "#/edit-prompt",
        "#/campaign-performance",
        "#/keyword-intelligence",
        "#/attribution-analysis",
        "#/market-watch",
      ];

      if (filterRoutes.includes(currentHash)) {
        console.log("🎯 User explicitly navigated to filter page:", currentHash);
        userNavigationRef.current = true;

        // Reset the flag after a delay to allow normal sheet-based navigation later
        setTimeout(() => {
          userNavigationRef.current = false;
        }, 2000);
      } else if (currentHash === "#/" || currentHash === "") {
        // User navigated back to home or cleared hash - reset the flag immediately
        console.log("🏠 User navigated to home page - resetting navigation flag");
        userNavigationRef.current = false;
      }
    };

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);

    // Check initial hash
    handleHashChange();

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Load insights history from API (prompt + data + insights) - used for History tab
  const loadPromptHistory = async () => {
    try {
      const response = await getInsightsHistory();
      const rawItems = (response.items || []) as any[];

      const mapped: PromptHistoryItem[] = rawItems.map((run: any) => {
        const id = run.id || run._id || run.run_id || "";
        const prompt = run.prompt || "";
        const shortName = run.shortName || "Insights run";

        const createdSource = run.createdAt || run.created_at || run.timestamp || Date.now();

        const createdAt =
          typeof createdSource === "number" ? createdSource : new Date(createdSource).getTime();

        return {
          id,
          prompt,
          shortName,
          createdAt,
        };
      });

      return mapped;
    } catch (error) {
      console.warn("Could not load prompt history from API:", error);
      return [] as PromptHistoryItem[];
    }
  };

  // Load prompt templates from /prompt_history (used for dropdown)
  const loadPromptTemplates = async () => {
    try {
      const response = await getPromptHistory(DEFAULT_PROMPT);
      return response.items || [];
    } catch (error) {
      console.warn("Could not load prompt templates from API:", error);
      return [] as PromptHistoryItem[];
    }
  };


  const savePromptHistory = async (items: PromptHistoryItem[]) => {
    try {
      if (items.length === 0) {
        await clearPromptHistoryApi(DEFAULT_PROMPT);
        setPromptHistory([]);
      } else {

        setPromptHistory(items);
      }
    } catch (error) {
      console.warn("Could not save prompt history:", error);
    }
  };

  // NEW: Function to load prompt data on-demand (lazy loading)
  const loadPromptDataIfNeeded = async () => {
    if (hasLoadedPromptData) {
      console.log("📚 Prompt data already loaded, skipping...");
      return; // Already loaded, skip
    }

    console.log("🔄 Loading prompt history and templates...");
    setHasLoadedPromptData(true);

    try {
      const [historyItems, templateItems] = await Promise.all([
        loadPromptHistory(),
        loadPromptTemplates(),
      ]);

      setPromptHistory(historyItems);
      setSavedPromptTemplates(templateItems);
      console.log("✅ Prompt data loaded successfully");
    } catch (error) {
      console.error("❌ Error loading prompt data:", error);
      // Reset flag on error so user can retry
      setHasLoadedPromptData(false);
    }
  };


  React.useEffect(() => {
    if (savedPromptsRefresh > 0) {
      loadPromptTemplates().then((items) => {
        setSavedPromptTemplates(items);
      });
    }
  }, [savedPromptsRefresh]);

  useEffect(() => {
    setDashboardStatus("");

    console.log("Page loaded/refreshed - resetting to dashboard templates view");

    const cleanupCustomProperties = async () => {
      try {
        await Excel.run(async (context) => {
          const customProperties = context.workbook.properties.custom;
          customProperties.load("items");
          await context.sync();
          const items = customProperties.items;
          const propertiesToDelete = [
            "showASINSKUFilter",
            "showCustomDateRange",
            "showEditPromptUI",
          ];
          for (let i = 0; i < items.length; i++) {
            if (propertiesToDelete.includes(items[i].key)) {
              console.log(`Cleaning up lingering property: ${items[i].key}`);
              items[i].delete();
            }
          }
          await context.sync();
        });
      } catch (error) {
        console.log("Could not clean up custom properties on mount:", error);
      }
    };
    Office.onReady().then(() => {
      cleanupCustomProperties();
    });
  }, []);

  // ==========================================
  // UPDATED detectCurrentDashboard FUNCTION
  // ==========================================

  const detectCurrentDashboard = async (allowNavFromHome: boolean = false) => {
    // Prevent concurrent executions
    if (isDetectingRef.current) {
      console.log("⏭️ Already detecting dashboard, skipping...");
      return;
    }
    if (isGeneratingDashboard || analysisInProgressRef.current) {
      console.log("⏳ Generation/analysis in progress – skipping detectCurrentDashboard navigation");
      return;
    }

    isDetectingRef.current = true;

    try {
      await Excel.run(async (context) => {
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();

        // Load BOTH name and id
        activeSheet.load("name");
        activeSheet.load("id");
        await context.sync();

        const currentSheetName = activeSheet.name;
        const currentSheetId = activeSheet.id;

        console.log("═══════════════════════════════════");
        console.log("📋 Sheet Name:", currentSheetName);
        console.log("🆔 Sheet ID:", currentSheetId);
        console.log("═══════════════════════════════════");

        let detectedDashboard: string | null = null;
        let detectedRoute: string = "/";

        // STEP 1: Check if we have this sheet ID in our mappings (PRIMARY METHOD)
        if (dashboardMappings[currentSheetId]) {
          const mapping = dashboardMappings[currentSheetId];
          detectedDashboard = mapping.type;
          detectedRoute = mapping.route;

          console.log("✅ Found by ID! Type:", detectedDashboard);

          // Check if name was changed
          if (mapping.originalName !== currentSheetName) {
            console.log("⚠️ Sheet was renamed!");
            console.log("   Original:", mapping.originalName);
            console.log("   Current:", currentSheetName);
          }
        }
        // STEP 2: Fallback to name-based detection (FALLBACK METHOD)
        else {
          console.log("🔍 Not found by ID, using name-based detection...");

          if (currentSheetName === "Dashboard") {
            detectedDashboard = "Sales Performance Dashboard";
            detectedRoute = "/sales-filter";
            try {
              await OfficeRuntime.storage.setItem(STORAGE_KEYS.FILTER_UI_SOURCE, FILTER_UI_SOURCES.DASHBOARD);
            } catch (_) {}
          } else if (currentSheetName === "Advertising Dashboard") {
            detectedDashboard = "Marketing Dashboard";
            detectedRoute = "/campaign-performance";
            try {
              await OfficeRuntime.storage.setItem(STORAGE_KEYS.FILTER_UI_SOURCE, FILTER_UI_SOURCES.DASHBOARD);
            } catch (_) {}
          } else if (currentSheetName === "Inventory") {
            detectedDashboard = "Inventory Dashboard";
            detectedRoute = "/";
            try {
              await OfficeRuntime.storage.setItem(STORAGE_KEYS.FILTER_UI_SOURCE, FILTER_UI_SOURCES.DASHBOARD);
            } catch (_) {}
          } else if (currentSheetName.startsWith("MW ")) {
            detectedDashboard = "Market Watch";
            detectedRoute = "/market-watch";
            try {
              await OfficeRuntime.storage.setItem(STORAGE_KEYS.FILTER_UI_SOURCE, FILTER_UI_SOURCES.DASHBOARD);
            } catch (_) {}
          } else {
            const { isRawDataSheet, isSalesDataSheet } = await import("../rowdata");
            if (isRawDataSheet(currentSheetName)) {
              if (isSalesDataSheet(currentSheetName) || /Sales Raw Data/i.test(currentSheetName)) {
                detectedDashboard = "Sales Performance Dashboard";
                detectedRoute = "/sales-filter";
              } else if (/Advertising Raw Data/i.test(currentSheetName)) {
                detectedDashboard = "Marketing Dashboard";
                detectedRoute = "/campaign-performance";
              } else {
                detectedDashboard = "Raw Data";
                detectedRoute = "/";
              }
              try {
                await OfficeRuntime.storage.setItem(STORAGE_KEYS.FILTER_UI_SOURCE, FILTER_UI_SOURCES.DATA);
              } catch (_) {}
            }
          }

          if (detectedDashboard && !dashboardMappings[currentSheetId]) {
            console.log("💾 Saving ID mapping for future use...");
            setDashboardMappings((prev) => ({
              ...prev,
              [currentSheetId]: {
                type: detectedDashboard!,
                route: detectedRoute,
                originalName: currentSheetName,
              },
            }));
          }
        }

        // STEP 3: Navigate if dashboard changed OR if we need to navigate to the correct route
        const dashboardChanged = detectedDashboard !== activeDashboard;
        const needsRouteNavigation = detectedDashboard && history.location.pathname !== detectedRoute;

        if (dashboardChanged) {
          setActiveDashboard(detectedDashboard);
        }

        if (dashboardChanged || needsRouteNavigation) {
          if (history.location.pathname === "/" && !allowNavFromHome) {
            console.log("🏠 Currently on home – skipping auto-navigation from detection");
            return;
          }

          if (userNavigationRef.current) {
            console.log(
              "🎯 User explicitly navigated to filter page – skipping auto-navigation from detection"
            );
            return;
          }

          if (history.location.pathname !== detectedRoute) {
            console.log("🎯 Navigating to:", detectedRoute);
            try {
              history.push(detectedRoute);
            } catch (e) {
              console.log("Could not navigate:", e);
            }
          }
        }
      });
    } catch (error) {
      console.error("Error detecting current dashboard:", error);
    } finally {
      isDetectingRef.current = false;
    }
  };

  // ==========================================
  // FUNCTION TO REGISTER DASHBOARD WHEN CREATED
  // ==========================================

  const registerDashboard = async (dashboardType: string, route: string) => {
    try {
      await Excel.run(async (context) => {
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();
        activeSheet.load("name");
        activeSheet.load("id");
        await context.sync();

        const sheetId = activeSheet.id;
        const sheetName = activeSheet.name;


        // Save to state
        setDashboardMappings((prev) => ({
          ...prev,
          [sheetId]: {
            type: dashboardType,
            route: route,
            originalName: sheetName,
          },
        }));

        const storageKey = `dashboard_mapping_${sheetId}`;
        await OfficeRuntime.storage.setItem(
          storageKey,
          JSON.stringify({
            type: dashboardType,
            route: route,
            originalName: sheetName,
            registeredAt: new Date().toISOString(),
          })
        );

        console.log("✅ Dashboard registered successfully!");
      });
    } catch (error) {
      console.error("Error registering dashboard:", error);
    }
  };

  // ==========================================
  // LOAD MAPPINGS FROM STORAGE ON STARTUP
  // ==========================================

  useEffect(() => {
    const loadDashboardMappings = async () => {
      try {
        await Excel.run(async (context) => {
          const worksheets = context.workbook.worksheets;
          worksheets.load("items");
          await context.sync();

          const mappings: DashboardMapping = {};

          // Load all saved mappings from storage
          for (const sheet of worksheets.items) {
            const sheetId = sheet.id;
            const storageKey = `dashboard_mapping_${sheetId}`;

            try {
              const stored = await OfficeRuntime.storage.getItem(storageKey);
              if (stored) {
                const data = JSON.parse(stored);
                mappings[sheetId] = {
                  type: data.type,
                  route: data.route,
                  originalName: data.originalName,
                };
                console.log("📚 Loaded mapping:", sheetId, "→", data.type);
              }
            } catch (e) {
              // Ignore if key doesn't exist
            }
          }

          setDashboardMappings(mappings);
          mappingsLoadedRef.current = true;

          await detectCurrentDashboard(false);
        });
      } catch (error) {
        console.error("Error loading dashboard mappings:", error);
      }
    };

    Office.onReady().then(() => {
      loadDashboardMappings();
    });
  }, []);

  useEffect(() => {
    let worksheetEventHandler: any = null;

    const checkForRibbonCommands = async () => {
      try {
        await Office.onReady();

        if (mappingsLoadedRef.current && !userNavigationRef.current) {
          await detectCurrentDashboard(false);
        }

        await Excel.run(async (context) => {
          worksheetEventHandler = context.workbook.worksheets.onActivated.add(async () => {
            try {

              if (!userNavigationRef.current) {
                await detectCurrentDashboard(true);
              }
              await updateDataSheets();
            } catch (error) {
              console.error("Error in worksheet activation handler:", error);
            }
          });
          await context.sync();
        });

        const updateDataSheets = async () => {
          try {
            await Excel.run(async (context) => {
              const worksheets = context.workbook.worksheets;
              worksheets.load("items/name");
              await context.sync();
              const dataSheets = worksheets.items
                .filter((sheet) => sheet.name.toLowerCase().includes("data"))
                .map((sheet) => sheet.name);
            });
          } catch (error) {
            console.error("Error updating data sheets:", error);
          }
        };

        await updateDataSheets();

        return async () => {
          if (worksheetEventHandler) {
            try {
              await Excel.run(async (context) => {
                context.workbook.worksheets.onActivated.remove(worksheetEventHandler);
                await context.sync();
              });
            } catch (error) {
              console.error("Error removing worksheet event handler:", error);
            }
          }
        };
      } catch (error) {
        console.error("Error in useEffect:", error);
        return undefined as any;
      }
    };
    checkForRibbonCommands();
  }, [activeDashboard]); 

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    try {
      await Excel.run(async (context) => {
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();
        activeSheet.load("name");
        await context.sync();
        const currentSheetName = activeSheet.name;
        console.log("Taskpane Refresh - Current active sheet:", currentSheetName);

        if (currentSheetName === "Inventory") {
          console.log("Taskpane: Refreshing Inventory Dashboard...");
          await generateInventoryDashboard();
          setDashboardStatus("Inventory Dashboard refreshed successfully!");
        } else if (currentSheetName === "Dashboard") {
          console.log("Taskpane: Refreshing Sales Performance Dashboard...");
          await createEnhancedSalesPerformanceDashboard();
          setDashboardStatus("Sales Performance Dashboard refreshed successfully!");
        } else if (currentSheetName === "Advertising Dashboard") {
          console.log("Taskpane: Refreshing Advertising Dashboard...");
          await generateMarketingDashboard();
          setDashboardStatus("Advertising Dashboard refreshed successfully!");
        } else {

          const { isRawDataSheet, isSalesDataSheet, refreshRawDataSheet, refreshSalesDataSheet } =
            await import("../rowdata");
          if (isRawDataSheet(currentSheetName)) {
            console.log(`Taskpane: Refreshing data sheet: ${currentSheetName}...`);

            const worksheets = context.workbook.worksheets;
            worksheets.load("items/name");
            await context.sync();

            const dashboardSheet = worksheets.items.find((sheet) => sheet.name === "Dashboard");
            let dateRange: string | null = null;

            if (dashboardSheet) {
              const startCell = dashboardSheet.getRange("I3");
              const endCell = dashboardSheet.getRange("K3");

              startCell.load(["values", "text"]);
              endCell.load(["values", "text"]);
              await context.sync();

              let startText = "";
              let endText = "";

              try {
                startText =
                  startCell.text && startCell.text[0] && startCell.text[0][0]
                    ? String(startCell.text[0][0])
                    : "";
                if (!startText) {
                  startText =
                    startCell.values && startCell.values[0] && startCell.values[0][0]
                      ? String(startCell.values[0][0])
                      : "";
                }
              } catch (e) {
                startText =
                  startCell.values && startCell.values[0] && startCell.values[0][0]
                    ? String(startCell.values[0][0])
                    : "";
              }

              try {
                endText =
                  endCell.text && endCell.text[0] && endCell.text[0][0]
                    ? String(endCell.text[0][0])
                    : "";
                if (!endText) {
                  endText =
                    endCell.values && endCell.values[0] && endCell.values[0][0]
                      ? String(endCell.values[0][0])
                      : "";
                }
              } catch (e) {
                endText =
                  endCell.values && endCell.values[0] && endCell.values[0][0]
                    ? String(endCell.values[0][0])
                    : "";
              }

              if (startText && endText && startText !== "undefined" && endText !== "undefined") {
                const formatDate = (dateStr: string) => {
                  try {
                    const num = parseFloat(dateStr);
                    if (!isNaN(num) && num > 10000) {
                      const excelEpoch = new Date(1900, 0, 1);
                      const date = new Date(excelEpoch.getTime() + (num - 2) * 24 * 60 * 60 * 1000);
                      return date.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      });
                    }

                    const date = new Date(dateStr);
                    if (!isNaN(date.getTime())) {
                      return date.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      });
                    }
                  } catch (e) {
                    // Return original string if parsing fails
                  }
                  return dateStr;
                };

                const formattedStart = formatDate(startText);
                const formattedEnd = formatDate(endText);
                dateRange = `${formattedStart} - ${formattedEnd}`;
              }
            }

            console.log("Date range for taskpane data refresh:", dateRange);

            if (isSalesDataSheet(currentSheetName)) {
              console.log("Refreshing Sales Data sheet (orders only)");
              await refreshSalesDataSheet(context, activeSheet, dateRange);
              setDashboardStatus("Sales Data refreshed successfully!");
            } else {
              console.log("Refreshing Raw Data sheet (products + orders)");
              await refreshRawDataSheet(context, activeSheet, dateRange);
              setDashboardStatus("Raw Data refreshed successfully!");
            }
          } else {
            console.log("Taskpane: Unknown sheet type - cannot refresh:", currentSheetName);
            setDashboardStatus(
              `Cannot refresh "${currentSheetName}" sheet. Please switch to a dashboard sheet to refresh.`
            );
            setTimeout(() => setDashboardStatus(""), 5000);
            setIsRefreshing(false);
            return;
          }
        }
        setLastUpdated(new Date().toLocaleString());
      });
      setTimeout(() => setDashboardStatus(""), 3000);
    } catch (error) {
      console.error("Error refreshing data:", error);
      setDashboardStatus("Error refreshing dashboard. Please try again.");
      setTimeout(() => setDashboardStatus(""), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  const createInventoryDashboard = async () => {
    console.log("Creating Inventory Dashboard...");
    await generateInventoryDashboard();
  };

  const handleGenerateDashboard = async (dashboardType: string) => {
    setActiveDashboard(dashboardType);

    setIsGeneratingDashboard(true);
    OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASH_FILTER);
    try {
      await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "true");
      const loaderName =
        dashboardType === "Marketing Dashboard"
          ? "Advertising Dashboard"
          : dashboardType === "Sales Performance Dashboard"
            ? "Sales Dashboard"
            : dashboardType || "Dashboard";
      await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME, loaderName);
    } catch (_) {}
    try {
      switch (dashboardType) {
        case "Sales Performance Dashboard":
          console.log("🎯 Step 1: Generating Sales Dashboard with full dataset...");
          await createEnhancedSalesPerformanceDashboard();

          console.log("📝 Step 2: Registering dashboard sheet ID...");
          await registerDashboard("Sales Performance Dashboard", "/sales-filter");

          console.log("✅ Step 3: Sales Dashboard created, navigating to filter UI...");
          try {
            history.push("/sales-filter");
          } catch (_) {
            // no-op
          }
          break;

        case "Marketing Dashboard":
          // Generate the dashboard FIRST
          console.log("🎯 Generating Advertising Dashboard...");
          await generateMarketingDashboard();

          // ✅ Register the sheet ID mapping
          await registerDashboard("Marketing Dashboard", "/campaign-performance");

          // Navigate to filter UI AFTER generation completes
          try {
            history.push("/campaign-performance");
          } catch (_) {
            // no-op
          }
          break;

        case "SKU Harmonization Tool":
          setDashboardStatus("SKU Harmonization coming soon...");
          break;

        case "Executive Summary":
          setDashboardStatus("Executive Summary coming soon...");
          break;

        case "Inventory Dashboard":
          await createInventoryDashboard();

          // ✅ Register the sheet ID mapping
          await registerDashboard("Inventory Dashboard", "/");
          break;

        default:
          setDashboardStatus("Unknown dashboard type");
      }

      setLastUpdated(new Date().toLocaleString());

      if (
        dashboardType === "Sales Performance Dashboard" ||
        dashboardType === "Marketing Dashboard" ||
        dashboardType === "Inventory Dashboard"
      ) {
        setTimeout(async () => {
          await monitorAndRepairCharts();
        }, POLLING.NAVIGATION_DELAY_MS);
      }

      setTimeout(() => setDashboardStatus(""), 3000);
    } catch (error) {
      console.error("Error generating dashboard:", error);
    } finally {
      setIsGeneratingDashboard(false);
      try {
        await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "false");
        await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME);
      } catch (_) {}
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "Select date";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleASINSKUSubmit = async () => {
    const skus = asinSkuValue.trim().split(",");
    const params = {
      skus: skus,
      start_purchase_date: startDate ? startDate.toISOString() : "",
      end_purchase_date: endDate ? endDate.toISOString() : "",
    };

    // Enhanced console logging for filter values
    console.log("🚀 APPLYING ASIN/SKU FILTER");
    console.log("📋 SKUs:", skus);
    console.log("📅 Start Date:", startDate);
    console.log("📅 End Date:", endDate);
    console.log("💾 Full Params:", params);

    OfficeRuntime.storage.setItem(STORAGE_KEYS.DASH_FILTER, JSON.stringify(params));

    // Store individual filter values for tracking
    const individualFilters = {
      asinSkuValues: skus,
      startDate: startDate,
      endDate: endDate,
      filterType: "ASIN/SKU",
      appliedAt: new Date().toISOString(),
    };
    OfficeRuntime.storage.setItem("individual_filters", JSON.stringify(individualFilters));

    try {
      await filteredDashboard(params);
      setShowFilterSuccess(true);
      const dateRangeText =
        startDate && endDate ? ` (${formatDate(startDate)} - ${formatDate(endDate)})` : "";

      // Log success
      console.log("✅ ASIN/SKU Filter applied successfully!");
      console.log("📊 Dashboard created with filters:", params);
    } catch (error) {
      console.error("❌ Error applying ASIN/SKU filter:", error);
    }
  };

  const handleCustomDateRangeSubmit = async () => {
    OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASH_FILTER);
    const params = {
      skus: [],
      start_purchase_date: startDate ? startDate.toISOString() : "",
      end_purchase_date: endDate ? endDate.toISOString() : "",
    };

    // Enhanced console logging for custom date range filter
    console.log("🚀 APPLYING CUSTOM DATE RANGE FILTER");
    console.log("📅 Start Date:", startDate);
    console.log("📅 End Date:", endDate);
    console.log("💾 Full Params:", params);

    // Store individual filter values for tracking
    const individualFilters = {
      asinSkuValues: [],
      startDate: startDate,
      endDate: endDate,
      filterType: "Custom Date Range",
      appliedAt: new Date().toISOString(),
    };
    OfficeRuntime.storage.setItem("individual_filters", JSON.stringify(individualFilters));

    try {
      await filteredDashboard(params);
      setShowFilterSuccess(true);

      // Log success
      console.log("✅ Custom Date Range Filter applied successfully!");
      console.log("📊 Dashboard created with date range:", params);
    } catch (error) {
      console.error("❌ Error applying custom date range filter:", error);
    }
  };

  const openInsightsDialogWithPrompt = async (
    userPrompt: string,
    dataKey: string | null = null,
    shortName?: string
  ) => {
    try {
      console.log("Navigating to AI Insights taskpane with custom prompt:", userPrompt);
      if (dataKey) {
        console.log("With data key:", dataKey);
      }

      const params = new URLSearchParams();
      params.append("user_prompt", userPrompt);

      if (dataKey) {
        params.append("data_key", dataKey);
      } else {
        // If no data key, indicate no data was selected
        params.append("no_data", "true");
      }

      if (shortName && shortName.trim()) {
        params.append("short_name", shortName.trim());
      }

      history.push(`/ai-insights?${params.toString()}`);

      return Promise.resolve();
    } catch (error) {
      console.error("Error in openInsightsDialogWithPrompt:", error);
      throw error;
    }
  };

  const hasDateRange = useMemo(() => Boolean(startDate && endDate), [startDate, endDate]);

  // Utility functions for displaying stored filter values
  const showAllDashboardSheets = async () => {
    try {
      const allSheets = await OfficeRuntime.storage.getItem("all_dashboard_sheets");
      if (allSheets) {
        const sheetsRegistry = JSON.parse(allSheets);
        console.log("=== ALL DASHBOARD SHEETS ===");
        console.table(sheetsRegistry);

        // Detailed view
        Object.entries(sheetsRegistry).forEach(([sheetId, info]: [string, any]) => {
          console.log(`\n📄 Sheet: ${info.sheetName}`);
          console.log(`🆔 ID: ${sheetId}`);
          console.log(`⏰ Created: ${info.createdAt}`);
          console.log(`📊 Filter Type: ${info.filterType}`);
          console.log(`📋 Filters:`, info.filters);
          console.log("---");
        });
      } else {
        console.log("No dashboard sheets found");
      }
    } catch (error) {
      console.error("Error retrieving sheet info:", error);
    }
  };

  const getCurrentSheetFilterValues = async () => {
    try {
      await Excel.run(async (context) => {
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();
        activeSheet.load("id,name");
        await context.sync();

        const sheetSpecificKey = `sheet_${activeSheet.id}`;
        const currentSheetFilters = await OfficeRuntime.storage.getItem(sheetSpecificKey);

        if (currentSheetFilters) {
          const filters = JSON.parse(currentSheetFilters);
          console.log("=== CURRENT SHEET FILTER VALUES ===");
          console.log(`📄 Sheet: ${activeSheet.name}`);
          console.log(`🆔 ID: ${activeSheet.id}`);
          console.log("📊 Filter Values:", filters);
        } else {
          console.log(`No filters found for current sheet: ${activeSheet.name}`);
        }
      });
    } catch (error) {
      console.error("Error getting current sheet filter values:", error);
    }
  };

  const showAllStoredFilters = async () => {
    try {
      const storedFilters = await OfficeRuntime.storage.getItem(STORAGE_KEYS.DASH_FILTER);
      const individualFilters = await OfficeRuntime.storage.getItem("individual_filters");

      console.log("=== ALL STORED FILTER VALUES ===");
      console.log(
        "📊 Current Dashboard Filter:",
        storedFilters ? JSON.parse(storedFilters) : "No current filter"
      );
      console.log(
        "🔍 Individual Filter Details:",
        individualFilters ? JSON.parse(individualFilters) : "No individual filters"
      );
    } catch (error) {
      console.error("Error retrieving stored filters:", error);
    }
  };

  // Make utility functions available globally for console access
  (window as any).showAllDashboardSheets = showAllDashboardSheets;
  (window as any).getCurrentSheetFilterValues = getCurrentSheetFilterValues;
  (window as any).showAllStoredFilters = showAllStoredFilters;

  return {
    isRefreshing,
    isGeneratingDashboard,
    lastUpdated,
    activeDashboard,
    dashboardStatus,
    asinSkuValue,
    startDate,
    endDate,
    showFilterSuccess,
    editPromptValue,
    promptHistory,
    savedPromptTemplates,
    savedPromptName,
    savedPromptsRefresh,
    setAsinSkuValue,
    setStartDate,
    setEndDate,
    setSavedPromptName,
    setEditPromptValue,
    setPromptHistory,
    setDashboardStatus,
    handleRefreshData,
    handleGenerateDashboard,
    handleASINSKUSubmit,
    handleCustomDateRangeSubmit,
    openInsightsDialogWithPrompt,
    hasDateRange,
    formatDate,
    savePromptHistory,
    setSavedPromptsRefresh,
    loadPromptDataIfNeeded, // NEW: Export the lazy loading function
  };
}